import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  QButton,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';

import { DocumentOverviewContext } from '../../Context';
import { shouldRenderUndoDeleteButton } from '../../RenderLogic';

import { documentApi } from '../../../../../api/document';
import { DocumentVersion } from '../../../../../api/model/document';
import { useFeatureFlagValue } from '../../../../../hooks/useFeatureFlagValue';
import { ButtonGroupContext } from '../../../Contexts/ButtonGroupContext';

type UndoDeleteButtonProps = {
  versions?: DocumentVersion[];
};

export const UndoDeleteButton = ({ versions = [] }: UndoDeleteButtonProps) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { qualioDocument } = useContext(DocumentOverviewContext);
  const { isDisabled, setDisabled } = useContext(ButtonGroupContext);
  const [isUndoingDelete, setIsUndoingDelete] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToastProvider();
  const isNewTemplateUpgradeEnabled = useFeatureFlagValue(
    'new-template-upgrade',
  );

  const commonToastProps = {
    id: 'undo-delete-toast',
    replace: true,
  };

  const handleUndoDelete = async () => {
    const { id: documentId, major_version, minor_version } = qualioDocument;
    const version = `${major_version}.${minor_version}`;
    setDisabled(true, setIsUndoingDelete);
    documentApi
      .undoDelete(documentId.toString(), version)
      .then(() => {
        setDisabled(false, setIsUndoingDelete);
        showToast({
          'data-cy': 'undo-delete-button-notify-success-toast',
          ...commonToastProps,
          status: 'success',
          title: t('notifications.success'),
          description: t('success.successfullyRestoredDocument'),
        });
        void navigate('/workspace');
      })
      .catch((e) => {
        setDisabled(false, setIsUndoingDelete);
        const failureMessage =
          e?.response?.data?.message || t('documentActions.failedToUndoDelete');
        showToast({
          'data-cy': 'undo-delete-button-notify-error-toast',
          ...commonToastProps,
          status: 'error',
          title: t('notifications.error'),
          description: failureMessage,
        });
      });
  };

  if (
    !shouldRenderUndoDeleteButton(
      qualioDocument,
      currentUser,
      versions,
      isNewTemplateUpgradeEnabled,
    )
  ) {
    return null;
  }

  return (
    <QButton
      isDisabled={isDisabled}
      isLoading={isUndoingDelete}
      variant="outline"
      onClick={handleUndoDelete}
      data-cy={'undo-delete-button'}
    >
      {t('buttons.undoDelete')}
    </QButton>
  );
};
